import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { addressSearchService, AddressSuggestion, AddressSearchResult } from '../utils/addressSearch';
import { useTranslation } from '../hooks/useTranslation';

interface AddressSearchInputProps {
  placeholder: string;
  value: string;
  onChange: (result: AddressSearchResult) => void;
  onInputChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

const AddressSearchInput: React.FC<AddressSearchInputProps> = ({
  placeholder,
  value,
  onChange,
  onInputChange,
  className = '',
  disabled = false
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle input change with debounced search
  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onInputChange?.(newValue);
    
    if (newValue.trim().length >= 3) {
      setIsLoading(true);
      setShowSuggestions(true);
      setSelectedIndex(-1);
      
      addressSearchService.debouncedSearch(
        newValue,
        (results) => {
          setSuggestions(results);
          setIsLoading(false);
        }
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    const result = addressSearchService.suggestionToResult(suggestion);
    setInputValue(result.address);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onChange(result);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`address-search-container ${showSuggestions ? 'has-suggestions' : ''} ${className}`}>
      <div className="address-search-input-group">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="address-search-input"
          autoComplete="off"
        />
        <div className="address-search-icon">
          {isLoading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <Search size={20} />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className="address-suggestions">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              className={`address-suggestion ${
                index === selectedIndex ? 'selected' : ''
              }`}
              onClick={() => handleSuggestionSelect(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <MapPin size={16} className="suggestion-icon" />
              <div className="suggestion-content">
                <div className="suggestion-address">
                  {addressSearchService.formatAddressSuggestion(suggestion)}
                </div>
                <div className="suggestion-details">
                  {suggestion.display_name}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && suggestions.length === 0 && !isLoading && inputValue.trim().length >= 3 && (
        <div className="address-suggestions">
          <div className="no-suggestions">
            <MapPin size={16} />
            <span>{t('address.noResults')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSearchInput;
